/**
 * BaaS Portal — Route Scope Matrix
 * Every /baas/* route must be listed here with its authorization level.
 *
 * Statuses:
 *   'authorized'   — route is active, tenant-scoped, and proof-verified
 *   'read_only'    — active but no write mutations permitted
 *   'access_denied' — route not yet authorized; renders AccessDenied screen
 */

export type BaaSRouteStatus = 'authorized' | 'read_only' | 'access_denied';

export interface BaaSRouteScope {
  path: string;
  label: string;
  status: BaaSRouteStatus;
  capability: 'read' | 'read+write';
  /** Supabase tables queried directly (should be empty for baas-api-only routes) */
  directTables: string[];
  /** baas-api endpoints invoked */
  baasApiActions: string[];
  /** How tenant isolation is enforced */
  tenantScope: string;
  notes?: string;
}

export const BAAS_ROUTE_SCOPES: BaaSRouteScope[] = [
  {
    path: '/baas',
    label: 'Dashboard',
    status: 'authorized',
    capability: 'read',
    directTables: ['baas_end_customers', 'baas_customer_links', 'baas_partner_audit_log'],
    baasApiActions: [],
    tenantScope: 'All queries filtered by partner_id = baasUser.partnerId from JWT-resolved baas_portal_users row',
    notes: 'Read-only stats aggregation. Direct table access scoped by RLS partner_id policy.',
  },
  {
    path: '/baas/customers',
    label: 'Customers',
    status: 'authorized',
    capability: 'read',
    directTables: ['baas_end_customers'],
    baasApiActions: [],
    tenantScope: '.eq("partner_id", partnerId) enforced in every query',
    notes: 'Search + filter. No mutations. Partner can only see their own customers.',
  },
  {
    path: '/baas/customers/new',
    label: 'New Client Onboarding',
    status: 'authorized',
    capability: 'read+write',
    directTables: [],
    baasApiActions: ['POST /baas-portal-onboard (upsert_client, presign_evidence, commit_evidence, submit_kyc, validate_and_onboard, issue_iban)'],
    tenantScope: 'JWT-resolved partner_id; all ops proxied through baas-portal-onboard with AAL2 + partner ownership checks',
    notes: 'BP-6 manual onboarding wizard. 5-step: Entity → Identity → Docs → Biometrics/Screening → Submit. SCA freshness gate on Submit and Issue IBAN. Reason mandatory on both. All ops logged with source=portal_manual.',
  },
  {
    path: '/baas/kyc',
    label: 'KYC Status',
    status: 'authorized',
    capability: 'read',
    directTables: ['baas_end_customers'],
    baasApiActions: ['GET /baas/v1/clients/:id/status'],
    tenantScope: '.eq("partner_id", partnerId) on all queries',
    notes: 'Aggregated KYC status view. Sumsub applicant IDs truncated for privacy.',
  },
  {
    path: '/baas/accounts',
    label: 'Accounts & IBANs',
    status: 'authorized',
    capability: 'read',
    directTables: ['baas_end_customers', 'ledger_accounts', 'account_identifiers'],
    baasApiActions: [],
    tenantScope: 'client_ids resolved from baas_end_customers WHERE partner_id = partnerId; downstream joins scoped to those IDs only',
    notes: 'Two-hop scoping: partner → end_customers → client_profile_id → ledger_accounts.',
  },
  {
    path: '/baas/alerts',
    label: 'Alerts & Cases',
    status: 'authorized',
    capability: 'read',
    directTables: ['baas_end_customers', 'screening_checks', 'compliance_cases'],
    baasApiActions: [],
    tenantScope: 'client_ids resolved from baas_end_customers; screening + cases filtered to those IDs',
    notes: 'Read-only compliance visibility. Partner cannot act on cases.',
  },
  {
    path: '/baas/exports',
    label: 'Exports',
    status: 'authorized',
    capability: 'read',
    directTables: ['baas_end_customers', 'ledger_accounts', 'account_identifiers'],
    baasApiActions: [],
    tenantScope: '.eq("partner_id", partnerId) on root query; all joined data inherits scoping',
    notes: 'CSV export only. No server-side write. Data never crosses partner boundary.',
  },
  {
    path: '/baas/audit',
    label: 'Audit Log',
    status: 'authorized',
    capability: 'read',
    directTables: ['baas_partner_audit_log'],
    baasApiActions: [],
    tenantScope: '.eq("partner_id", partnerId)',
    notes: 'Immutable log. Partner sees only their own API audit trail.',
  },
  {
    path: '/baas/settings',
    label: 'Settings',
    status: 'authorized',
    capability: 'read',
    directTables: [],
    baasApiActions: [],
    tenantScope: 'baasUser context from BaaSAuthContext — no DB queries',
    notes: 'Displays static partner metadata from JWT-resolved baas_portal_users. API key management pending.',
  },
  {
    path: '/baas/manual-ops',
    label: 'Manual Ops',
    status: 'authorized',
    capability: 'read+write',
    directTables: ['baas_manual_ops_log', 'baas_partner_api_health'],
    baasApiActions: ['POST /baas-manual-ops', 'GET /baas-manual-ops/health'],
    tenantScope: 'JWT-resolved partner_id; all ops proxied through baas-manual-ops edge function with partner_id scoping',
    notes: 'Manual fallback operations. Requires baas.manual_ops permission or admin/owner role. Rate-limited 20/min. All ops logged to baas_events + partner_audit_log with source=portal_manual.',
  },
  {
    path: '/baas/sandbox',
    label: 'Sandbox',
    status: 'authorized',
    capability: 'read+write',
    directTables: [],
    baasApiActions: ['POST /baas-diagnostics/test-request', 'POST /baas-diagnostics/test-signature'],
    tenantScope: 'JWT-resolved partner_id; all test runs logged as api_test_run events',
    notes: 'Interactive API console with dry-run validation. Signature tester. API reference docs. Every test run audited to baas_events.',
  },
  {
    path: '/baas/status',
    label: 'API Status',
    status: 'authorized',
    capability: 'read',
    directTables: ['baas_partner_audit_log', 'baas_events', 'baas_partner_api_health'],
    baasApiActions: ['GET /baas-diagnostics/health', 'GET /baas-diagnostics/last-errors'],
    tenantScope: 'JWT-resolved partner_id; all queries scoped by partner_id',
    notes: 'API health indicator, rate-limit counters, last 20 errors, signature failures, replay attempts.',
  },
  {
    path: '/baas/payments',
    label: 'Payments',
    status: 'authorized',
    capability: 'read+write',
    directTables: [],
    baasApiActions: ['POST /baas-portal-payment', 'GET /baas-portal-payment/history'],
    tenantScope: 'JWT-resolved partner_id; payment_intent scoped per partner via baas-portal-payment EF; all ops logged to baas_events + partner_audit_log',
    notes: 'SEPA outbound submission. Settlement driven by provider webhook/camt. payment_intent lifecycle: queued→submitted→accepted→settled|rejected|returned.',
  },
  {
    path: '/baas/requests',
    label: 'Requests',
    status: 'authorized',
    capability: 'read+write',
    directTables: ['baas_partner_requests'],
    baasApiActions: [],
    tenantScope: '.eq("partner_id", partnerId) on all queries; RLS enforces partner boundary at DB level',
    notes: 'Partner service requests (IBAN disputes, KYC appeals, onboarding exceptions). Realtime-enabled for status updates.',
  },
  {
    path: '/baas/cases',
    label: 'Cases & Appeals',
    status: 'authorized',
    capability: 'read',
    directTables: ['baas_end_customers', 'compliance_cases'],
    baasApiActions: [],
    tenantScope: 'Two-hop: client_ids from baas_end_customers WHERE partner_id=partnerId; compliance_cases filtered to those client_ids',
    notes: 'Read-only case visibility for partner. Partner cannot act, close, or escalate cases. Internal investigation details redacted.',
  },
  {
    path: '/baas/safeguarding',
    label: 'Safeguarding',
    status: 'authorized',
    capability: 'read+write',
    directTables: ['baas_partner_safeguarding_accounts', 'baas_partner_sweep_policies', 'baas_partner_sweep_runs'],
    baasApiActions: ['GET /baas-sweep/status', 'POST /baas-sweep/request'],
    tenantScope: 'JWT-resolved partner_id; all queries + mutations scoped by partner_id via RLS and edge function',
    notes: 'Pool account view, sweep policy, sweep history, manual sweep requests with optional 4-eyes approval.',
  },
  {
    path: '/baas/support',
    label: 'Support',
    status: 'authorized',
    capability: 'read+write',
    directTables: ['partner_tickets', 'partner_ticket_messages', 'partner_notifications'],
    baasApiActions: [],
    tenantScope: 'JWT-resolved partner_id; all queries scoped by partner_id via RLS',
    notes: 'Ticket creation, message thread, notifications bell. Realtime-enabled.',
  },
  {
    path: '/baas/billing',
    label: 'Billing & Collections',
    status: 'authorized',
    capability: 'read',
    directTables: ['baas_collections', 'baas_collection_policies', 'baas_billing_runs'],
    baasApiActions: ['GET /baas-collections/status'],
    tenantScope: 'JWT-resolved partner_id; all queries scoped by partner_id via RLS',
    notes: 'Read-only billing view. Collections, invoices, policy summary. Pool balance from edge function.',
  },
];

/** Routes NOT yet authorized — will show AccessDenied */
export const DENIED_BAAS_ROUTES = BAAS_ROUTE_SCOPES
  .filter(r => r.status === 'access_denied')
  .map(r => r.path);

export function getBaaSRouteScope(path: string): BaaSRouteScope | undefined {
  return BAAS_ROUTE_SCOPES.find(r => r.path === path);
}

export function isBaaSRouteAuthorized(path: string): boolean {
  const scope = getBaaSRouteScope(path);
  return !!scope && scope.status !== 'access_denied';
}
